/**
 * Solana gRPC add-on management page (authenticated).
 *
 * Route: /solana-grpc
 *   * Not subscribed → Subscribe card + "$199/month" Stripe Checkout CTA
 *   * Subscribed     → Status card + connection guide + Cancel CTA
 *
 * The gRPC add-on is DISTINCT from the JSON-RPC Plan tier. A customer can
 * hold both at once — this page never touches Subscription. See
 * datanodes-deployment docs/solana-grpc-launch.md for the full
 * architecture.
 *
 * Stripe redirect handling:
 *   Success: returns to /solana-grpc?session_id=cs_... → we refresh state
 *            once. The webhook is the source of truth for the row; this
 *            just pulls the fresh GET so the UI reflects it fast.
 *   Cancel:  returns to /solana-grpc (no query param) → same view as the
 *            initial not-subscribed state.
 */

import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import stripeService from "../services/stripeService";
import { useOrganization } from "../context/OrganizationContext";

// Same endpoint every customer connects to. Region routing (EU primary +
// US failover) is baked into the HAProxy backend — the customer never
// picks a region themselves. Prod / dev / staging differ only in the
// hostname; wire from env once we have separate PROD deploys.
const GRPC_ENDPOINT = "https://solana-grpc.datanodes.nodefleet.net:443";

// Small preformatted code block used by the connection guide.
const CodeBlock = ({ children }) => (
  <pre className="mt-2 overflow-x-auto rounded-sm border border-white/10 bg-black/40 p-3 font-mono text-[12px] leading-[1.55] text-gray-200">
    {children}
  </pre>
);

const Check = () => (
  <svg
    className="mt-[3px] h-4 w-4 shrink-0 text-primary"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    aria-hidden
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
          d="M5 13l4 4L19 7" />
  </svg>
);

const Bullet = ({ children }) => (
  <li className="flex items-start gap-2.5 text-[14px] leading-[1.45] text-white">
    <Check />
    <span>{children}</span>
  </li>
);

const SolanaGrpc = () => {
  const { currentOrganization } = useOrganization();
  const orgId = currentOrganization?.id;
  const [searchParams] = useSearchParams();

  const [status, setStatus] = useState(null); // { active, max_streams, ... } | null
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);

  // Owner-only CTAs. Non-owners see the state but can't subscribe or
  // cancel — the backend gate is the source of truth (403 owner_only).
  const userEmail = (localStorage.getItem("userEmail") || "").toLowerCase();
  const isOwner = !!(
    currentOrganization?.owner_email &&
    currentOrganization.owner_email.toLowerCase() === userEmail
  );

  const reload = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      const res = await stripeService.getGrpcAddonStatus(orgId);
      setStatus(res);
    } catch (err) {
      console.error("gRPC addon status load failed", err);
      toast.error("Failed to load Solana gRPC add-on state");
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    reload();
  }, [reload]);

  // If we came back from Stripe Checkout success, the row may take up to
  // a second for the webhook to write. Do a delayed re-fetch so the UI
  // flips from "Subscribe" to "Active" without a manual refresh.
  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    if (!sessionId) return;
    const t = setTimeout(() => {
      reload();
      toast.success("Solana gRPC add-on activated");
    }, 1500);
    return () => clearTimeout(t);
  }, [searchParams, reload]);

  const handleSubscribe = async () => {
    if (!orgId) return;
    setProcessing(true);
    try {
      const successUrl = `${window.location.origin}/#/solana-grpc?activated=1`;
      const cancelUrl = `${window.location.origin}/#/solana-grpc`;
      const session = await stripeService.createGrpcAddonCheckoutSession(
        orgId, successUrl, cancelUrl,
      );
      window.location.href = session.url;
    } catch (err) {
      console.error("gRPC addon checkout failed", err);
      toast.error(err?.message || "Failed to start checkout");
      setProcessing(false);
    }
  };

  const handleCancel = async () => {
    if (!orgId) return;
    if (!window.confirm(
      "Cancel your Solana gRPC add-on? You'll keep access until the end " +
      "of the current billing period.",
    )) return;
    setProcessing(true);
    try {
      const res = await stripeService.cancelGrpcAddon(orgId);
      if (res?.success) {
        toast.success(res.message || "Cancellation scheduled");
        await reload();
      } else {
        toast.error(res?.message || "Cancel failed");
      }
    } catch (err) {
      console.error("gRPC addon cancel failed", err);
      toast.error(err?.message || "Failed to cancel");
    } finally {
      setProcessing(false);
    }
  };

  // Derived states — split for clarity.
  const isActive = !!status?.active;
  const cancelScheduled = !!status?.cancel_at_period_end;
  const stripeStatus = status?.stripe_status;

  return (
    <div className="min-h-screen bg-black px-6 py-12">
      <div className="mx-auto max-w-4xl">
        <header className="mb-10">
          <p className="mb-2 text-[13px] uppercase tracking-[1.5px] text-primary">
            Solana gRPC
          </p>
          <h1 className="font-title text-3xl font-bold text-white sm:text-4xl">
            {isActive ? "Solana gRPC Starter" : "Subscribe to Solana gRPC Starter"}
          </h1>
          <p className="font-secondary mt-3 max-w-2xl text-[15px] leading-relaxed text-gray-300">
            {isActive
              ? "Real-time Solana data streaming from our bare-metal Agave validators."
              : "$199/month. Real-time Solana data streaming from our bare-metal Agave validators — EU primary with US failover."}
          </p>
        </header>

        {loading ? (
          <div className="rounded-sm border border-white/10 bg-landing-section p-8">
            <p className="text-center text-gray-400">Loading…</p>
          </div>
        ) : !isActive ? (
          /* ================================================================
             NOT-SUBSCRIBED — subscribe card
             ================================================================ */
          <section className="rounded-sm border border-white/10 bg-landing-section p-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2">
                <p className="font-secondary text-[14px] uppercase tracking-[1.2px] text-primary">
                  What's included
                </p>
                <ul className="mt-4 flex flex-col gap-[10px]">
                  <Bullet>1 concurrent gRPC stream</Bullet>
                  <Bullet>Up to 5 accounts or owners per filter</Bullet>
                  <Bullet>Real-time subscriptions: accounts, transactions, slots, block metadata</Bullet>
                  <Bullet>EU primary, US automatic failover</Bullet>
                  <Bullet>Email support (24h response)</Bullet>
                </ul>
                <p className="font-secondary mt-6 text-[13px] leading-[1.55] text-gray-400">
                  Uses your existing NodeFleet API key for authentication —
                  no separate credential to manage. Your JSON-RPC plan is
                  unaffected (this is a parallel subscription).
                </p>
              </div>
              <div className="flex flex-col justify-center rounded-sm border border-white/10 bg-black/40 p-6">
                <p className="font-secondary text-[13px] uppercase tracking-[1.2px] text-gray-400">
                  Monthly
                </p>
                <p className="font-title mt-1 text-[36px] leading-none text-white">
                  $199
                </p>
                <p className="font-secondary mt-1 text-[13px] text-gray-400">
                  Billed monthly · cancel anytime
                </p>
                <button
                  type="button"
                  disabled={processing || !isOwner}
                  onClick={handleSubscribe}
                  className="mt-5 inline-flex items-center justify-center rounded-sm bg-primary px-6 py-3 font-secondary text-[14px] font-semibold text-black transition-colors hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {processing ? "Redirecting…" : "Subscribe"}
                </button>
                {!isOwner && (
                  <p className="font-secondary mt-3 text-[12px] text-gray-500">
                    Only the organization owner can subscribe.
                  </p>
                )}
              </div>
            </div>
          </section>
        ) : (
          /* ================================================================
             SUBSCRIBED — status + connection guide + cancel
             ================================================================ */
          <div className="space-y-6">
            <section className="rounded-sm border border-white/10 bg-landing-section p-8">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <p className="font-secondary text-[13px] uppercase tracking-[1.2px] text-primary">
                    Active
                  </p>
                  <p className="font-title mt-1 text-[24px] leading-tight text-white">
                    Solana gRPC Starter
                  </p>
                  <p className="font-secondary mt-1 text-[14px] text-gray-400">
                    {status.max_streams} concurrent stream · ${status.price_usd}/month
                    {stripeStatus && ` · Stripe: ${stripeStatus}`}
                  </p>
                  {cancelScheduled && status.current_period_end && (
                    <p className="font-secondary mt-3 text-[13px] text-amber-400">
                      Cancels at end of billing period
                      ({new Date(status.current_period_end).toLocaleDateString()})
                    </p>
                  )}
                </div>
                {isOwner && !cancelScheduled && (
                  <button
                    type="button"
                    disabled={processing}
                    onClick={handleCancel}
                    className="rounded-sm border border-white/15 bg-transparent px-4 py-2 font-secondary text-[13px] text-gray-300 transition-colors hover:border-white/40 hover:text-white disabled:opacity-60"
                  >
                    {processing ? "Working…" : "Cancel subscription"}
                  </button>
                )}
              </div>
            </section>

            <section className="rounded-sm border border-white/10 bg-landing-section p-8">
              <h2 className="font-title text-[20px] text-white">Connect</h2>
              <p className="font-secondary mt-2 text-[14px] leading-relaxed text-gray-300">
                Point any yellowstone-grpc client at the endpoint below and
                send your NodeFleet API key as either{" "}
                <code className="font-mono text-primary">x-api-key</code> or{" "}
                <code className="font-mono text-primary">x-token</code>{" "}
                gRPC metadata on every subscribe.
              </p>

              <div className="mt-6">
                <p className="font-secondary text-[13px] uppercase tracking-[1.2px] text-gray-400">
                  Endpoint
                </p>
                <CodeBlock>{GRPC_ENDPOINT}</CodeBlock>
              </div>

              <div className="mt-6">
                <p className="font-secondary text-[13px] uppercase tracking-[1.2px] text-gray-400">
                  grpcurl example (list slot updates)
                </p>
                <CodeBlock>
{`grpcurl \\
  -H "x-api-key: sk_live_..." \\
  -d '{"slots":{"client":{}}}' \\
  ${GRPC_ENDPOINT.replace(/^https?:\/\//, "")} \\
  geyser.Geyser/Subscribe`}
                </CodeBlock>
              </div>

              <div className="mt-6">
                <p className="font-secondary text-[13px] uppercase tracking-[1.2px] text-gray-400">
                  Node.js @grpc/grpc-js
                </p>
                <CodeBlock>
{`import { credentials, Metadata } from '@grpc/grpc-js';
import { GeyserClient } from './generated/geyser_grpc_pb.js';

const meta = new Metadata();
meta.set('x-api-key', process.env.NF_API_KEY);

const client = new GeyserClient(
  'solana-grpc.datanodes.nodefleet.net:443',
  credentials.createSsl(),
);
const stream = client.subscribe(meta);
stream.on('data', (u) => console.log(u));
stream.write({ slots: { client: {} } });`}
                </CodeBlock>
              </div>

              <p className="font-secondary mt-6 text-[13px] leading-[1.55] text-gray-400">
                <span className="font-semibold text-gray-300">Limits:</span>{" "}
                1 concurrent stream · up to 5 accounts or owners per filter ·
                accounts, transactions, slots, and block-metadata subscriptions.
                Blocks-with-transactions is disabled on this tier — need it?{" "}
                <a
                  href="mailto:nodes@nodefleet.net?subject=Dedicated%20Solana%20gRPC%20cluster"
                  className="text-primary hover:underline"
                >
                  Contact us
                </a>{" "}
                about a Dedicated cluster.
              </p>
            </section>
          </div>
        )}
      </div>
    </div>
  );
};

export default SolanaGrpc;
